import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import { captureRateLimited } from '@/lib/sentry-capture-rate-limit';
import { useAdminStatus } from './use-admin-status';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  /**
   * Email-code (OTP) sign-in — replaces the deprecated magic-link path
   * (2026-05-29). Sends a 6-digit code; the caller then verifies it with
   * `verifyEmailOtp`. See `sendEmailOtp` impl for why a code beats a link.
   */
  sendEmailOtp: (email: string) => Promise<{ error: Error | null }>;
  /** Verify the 6-digit email sign-in code from `sendEmailOtp`. */
  verifyEmailOtp: (
    email: string,
    token: string
  ) => Promise<{ error: Error | null }>;
  /**
   * Verify the 6-digit email-confirmation code sent after self-signup
   * (`type:'signup'`). Confirms the email + establishes a session. Distinct
   * from `verifyEmailOtp` (`type:'email'`, sign-in) — the signup code is a
   * different GoTrue token type.
   */
  verifyEmailSignupOtp: (
    email: string,
    token: string
  ) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    meta: {
      firstName: string;
      lastName: string;
      /**
       * Optional wizard answers + ToS timestamp threaded into Supabase Auth
       * user_metadata so the `handle_new_user` DB trigger can write them
       * into `user_profiles` atomically with the auth.users INSERT — closes
       * the email-confirm round-trip gap where the wizard answers would
       * otherwise be lost (fix shipped 2026-05-14, see
       * `supabase/migrations/20260615000000_user_profile_signup_trigger_extended.sql`).
       * Omit on admin-invite or any flow where wizard answers aren't yet
       * known — the trigger writes only the minimal pending row in that
       * case, and the backfill wizard fills the rest later.
       */
      profile?: {
        firm_name: string;
        role: string;
        asset_classes: string;
        markets: string;
        focus: string;
        volume: string;
        terms_accepted_at: string;
      };
    }
  ) => Promise<{
    error: Error | null;
    session: Session | null;
    /**
     * `user.id` from the Supabase signUp response. Available even when
     * email-confirm is required (no session) — Supabase returns the
     * created auth.users row's id immediately. Web uses this to fire
     * the admin-notification POST after signUp succeeds. (2026-05-14)
     */
    userId: string | null;
  }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 2026-05-28 — auth-bootstrap resilience fix. Pre-fix: bare `.then()` with
    // no `.catch()` meant that if `getSession()` ever rejected, `loading` never
    // flipped to false and the `__root.tsx:130` "Loading..." screen rendered
    // forever (the cascade is gated on `loading`). The Sentry session replay
    // for paul/richard's stuck-sign-in (event 77800e0a…, replay
    // 3bd552c9ef004d73a70195e86cc9855c, 2026-05-28T14:08:32Z) captured this
    // exactly: page sat on "Loading…" indefinitely after GoTrue's lock-steal
    // recovery fired an AbortError that bubbled as an unhandled rejection.
    //
    // The lock-steal itself is a known + designed mechanism in
    // `@supabase/gotrue-js` — when a `navigator.locks.request()` lock has been
    // held longer than `lockTimeout` (default 5s), the library calls
    // `request(..., { steal: true })` to forcibly take it. The previous lock
    // holder's promise rejects with `AbortError: Lock broken by another
    // request with the 'steal' option`. This commonly fires under:
    //   - corporate browser-isolation extensions stalling fetch (Richard's
    //     case — Colliers-issued Windows 7 / Chrome 140 / "antifingerprint"
    //     extension on the page)
    //   - multi-tab races (one tab's refresh outlives the other's timeout)
    //   - stuck ServiceWorker holding storage access
    //   - genuine network blip during initial session probe
    //
    // The recovery path: the `onAuthStateChange` listener below ALSO fires
    // `INITIAL_SESSION` on mount, so a stranded `getSession()` is non-fatal
    // — the listener will populate session/user when GoTrue settles. We just
    // need to make sure `loading` flips to false so the cascade can render
    // the right route (worst case `/login`, which is the correct response to
    // "no session resolved").
    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        setSession(s);
        setUser(s?.user ?? null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        // ALWAYS exit the loading state — never strand the user on the
        // full-screen "Loading…" spinner. onAuthStateChange will populate
        // session/user when GoTrue's INITIAL_SESSION event fires.
        setLoading(false);

        const message =
          err instanceof Error ? err.message : String(err ?? 'unknown');
        const name = err instanceof Error ? err.name : 'unknown';
        const isKnownLockSteal =
          name === 'AbortError' &&
          /Lock broken by another request with the 'steal' option/.test(
            message
          );

        if (isKnownLockSteal) {
          // Known-transient GoTrue recovery — keep visibility (so we can
          // track frequency, correlate with user reports, see
          // browser/extension patterns) but at WARNING level so it doesn't
          // page anyone. Rate-limited (60s per process) so a chatty client
          // doesn't flood the project quota.
          captureRateLimited('auth_lock_steal_recovered', () => {
            Sentry.captureMessage('auth_lock_steal_recovered', {
              level: 'warning',
              tags: { surface: 'auth-bootstrap' },
              extra: {
                user_agent: navigator.userAgent,
                visibility_state: document.visibilityState,
              },
            });
          });
        } else {
          // Unknown bootstrap failure — surface as a normal exception so
          // it pages / shows up in the issue feed.
          Sentry.captureException(err, {
            tags: { surface: 'auth-bootstrap' },
          });
        }
      });

    // auth-flow-hardening Inc-5 (2026-05-20): explicit event handling.
    // The Supabase event types we receive (per @supabase/supabase-js docs):
    //   INITIAL_SESSION    — fires once on mount with the result of getSession
    //   SIGNED_IN          — user signed in (password, magic link, OAuth, etc.)
    //   SIGNED_OUT         — user signed out OR session was revoked
    //   TOKEN_REFRESHED    — JWT silently refreshed (every ~hour for active sessions)
    //   USER_UPDATED       — user metadata changed (rare; fires from updateUser)
    //   PASSWORD_RECOVERY  — fires when the user lands from a recovery-link;
    //                       the session at this moment is RECOVERY-tier (limited
    //                       scope: can call updateUser({password}) but the
    //                       reset-password.tsx page handles this locally and
    //                       this provider's job is just to keep `user` accurate)
    //
    // Catch-all setState handles all 6 — but we explicitly DON'T have a
    // PASSWORD_RECOVERY branch here because the reset-password page handles
    // it locally. Adding a branch here could fire before the local listener
    // and cause a race where the global state updates first (e.g. the
    // redirect cascade in __root.tsx might see `user` populated + bounce
    // the user to /cockpit before they can set their password).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      // SIGNED_OUT is the only event where stale React-Query caches need
      // explicit clearing — token refresh + user update both keep the
      // same user.id so per-user query keys stay valid. Cache clearing is
      // owned by the consumers (each hook subscribes via TanStack Query;
      // `useAuth().user?.id` is the canonical reactive trigger).
      if (
        process.env.NODE_ENV !== 'production' &&
        event !== 'TOKEN_REFRESHED'
      ) {
        // Dev-only: surface every non-noise auth event so we can debug
        // the recovery flow without inspecting localStorage. TOKEN_REFRESHED
        // fires every ~hour and is pure noise.
        // eslint-disable-next-line no-console
        console.debug('[auth] event:', event, 'user:', s?.user?.id ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // `isAdmin` was pre-2026-05-13 derived synchronously from the JWT
  // `app_metadata.role` claim. The JWT only refreshes on re-login, so admin
  // promote/demote changes silently failed to propagate. Phase 5 Session 4
  // replaced the JWT path with a DB-backed read through `useAdminStatus` —
  // the surface (`useAuth().isAdmin`) is preserved so every downstream
  // consumer (`Sidebar.tsx`, `admin.tsx`, `builder.tsx`, `settings.tsx`,
  // 5+ test mocks) continues to work without changes.
  //
  // `useAdminStatus` takes `user?.id` as an arg (not via its own `useAuth`)
  // to avoid a circular import; see the hook's JSDoc for rationale.
  const { isAdmin } = useAdminStatus(user?.id);
  const isApproved = useMemo(
    () => user?.app_metadata?.approved === true,
    [user]
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const sendEmailOtp = useCallback(async (email: string) => {
    // Email-code sign-in (2026-05-29) — replaces the magic-link path.
    //
    // WHY a code, not a link: corporate email security gateways
    // (Mimecast / Proofpoint / Microsoft Safe Links — e.g. Colliers) pre-
    // fetch every URL in an inbound email to sandbox it. That GET consumes
    // Supabase's single-use magic-link token, so when the human later
    // clicks, the token is already spent → sign-in silently fails (the
    // recipient lands logged-out or bounced). A 6-digit code can't be
    // "clicked" by a scanner, so it survives the gateway intact.
    //
    // The link-vs-code distinction is driven ENTIRELY by the email
    // template (`{{ .Token }}` vs `{{ .ConfirmationURL }}`), NOT by this
    // call. We drop `emailRedirectTo` (there is no link to redirect) and
    // set `shouldCreateUser: false` so this SIGN-IN control never silently
    // provisions an account — account creation lives at `/signup`.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    // `type: 'email'` is the verification type for signInWithOtp codes —
    // distinct from `'recovery'` (password reset) and `'signup'`. On
    // success GoTrue establishes a full session + fires SIGNED_IN, which
    // the onAuthStateChange listener above folds into `user`/`session`.
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const verifyEmailSignupOtp = useCallback(
    async (email: string, token: string) => {
      // `type: 'signup'` — the verification type for the post-signUp
      // email-confirmation code (distinct from `'email'` sign-in OTP). On
      // success GoTrue marks the email confirmed + establishes a full session.
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup',
      });
      return { error: error ? new Error(error.message) : null };
    },
    []
  );

  const signUp = useCallback<AuthContextValue['signUp']>(
    async (email, password, meta) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // 2026-05-29: NO `emailRedirectTo` — email confirmation is a 6-digit
          // CODE now, not a clickable link (corporate scanners pre-fetch+burn
          // links). The "Confirm your email" interstitial collects the code and
          // calls `verifyEmailSignupOtp` (verifyOtp type:'signup'). With no
          // redirect there's no `/auth/callback?type=signup` round-trip.
          data: {
            first_name: meta.firstName,
            last_name: meta.lastName,
            full_name: `${meta.firstName} ${meta.lastName}`,
            // Wizard answers + ToS timestamp — when present, the
            // `handle_new_user` DB trigger writes them into user_profiles
            // atomically with the auth.users INSERT (no PATCH needed).
            ...(meta.profile
              ? {
                  firm_name: meta.profile.firm_name,
                  role: meta.profile.role,
                  asset_classes: meta.profile.asset_classes,
                  markets: meta.profile.markets,
                  focus: meta.profile.focus,
                  volume: meta.profile.volume,
                  terms_accepted_at: meta.profile.terms_accepted_at,
                }
              : {}),
          },
        },
      });
      // `data.session` is set when Supabase auto-confirms the account
      // (local dev default + projects with email-confirmation disabled).
      // null when email confirmation is required (Supabase prod default).
      // `data.user.id` is set in BOTH branches — populated as soon as the
      // auth.users row is created (pre-email-confirm).
      return {
        error: error ? new Error(error.message) : null,
        session: data?.session ?? null,
        userId: data?.user?.id ?? null,
      };
    },
    []
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isAdmin,
        isApproved,
        signIn,
        sendEmailOtp,
        verifyEmailOtp,
        verifyEmailSignupOtp,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
